import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Hash, Wrench, LogIn, LogOut, Trash2, Edit2, Save,
  Building2, User, Calendar, DollarSign, Printer, X, Check, History,
} from 'lucide-react';
import RelatedLists from '../../components/RelatedLists';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLORS = {
  in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  assigned: 'bg-sky-50 text-sky-700 border-sky-200',
  maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
  retired: 'bg-slate-50 text-slate-600 border-slate-200',
  disposed: 'bg-slate-50 text-slate-500 border-slate-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [maintOpen, setMaintOpen] = useState(false);
  const [checkoutUser, setCheckoutUser] = useState('');
  const [maintNote, setMaintNote] = useState('');
  const [maintDate, setMaintDate] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assets/${id}`, { headers: authHeader() });
      setAsset(r.data);
      setForm({
        name: r.data.name || '', manufacturer: r.data.manufacturer || '', model: r.data.model || '',
        serial_number: r.data.serial_number || '', description: r.data.description || '',
        purchase_date: r.data.purchase_date?.slice(0, 10) || '',
        purchase_cost: r.data.purchase_cost || '',
        vendor: r.data.vendor || '', purchase_order: r.data.purchase_order || '',
        warranty_expires_at: r.data.warranty_expires_at?.slice(0, 10) || '',
        category_id: r.data.category_id || '',
        notes: r.data.notes || '',
      });
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load asset'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/assets/categories`, { headers: authHeader() }).then(r => setCategories(r.data.categories || []));
    axios.get(`${API_URL}/api/admin/users`, { headers: authHeader() }).then(r => setUsers(r.data.users || r.data || [])).catch(() => {});
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.purchase_cost === '') payload.purchase_cost = null;
      else if (payload.purchase_cost) payload.purchase_cost = parseFloat(payload.purchase_cost);
      await axios.patch(`${API_URL}/api/assets/${id}`, payload, { headers: authHeader() });
      toast.success('Saved');
      setEditing(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    if (!checkoutUser) { toast.error('Pick a user'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/assets/${id}/checkout`, { assigned_to_user_id: checkoutUser }, { headers: authHeader() });
      toast.success('Checked out');
      setCheckoutOpen(false); setCheckoutUser('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Checkout failed'); }
    finally { setBusy(false); }
  };

  const checkin = async () => {
    if (!window.confirm('Check this asset back in?')) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/assets/${id}/checkin`, {}, { headers: authHeader() });
      toast.success('Checked in');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Check-in failed'); }
    finally { setBusy(false); }
  };

  const submitMaint = async () => {
    if (!maintNote.trim()) { toast.error('Note required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/assets/${id}/maintenance`, {
        note: maintNote,
        next_service_date: maintDate ? new Date(maintDate).toISOString() : null,
      }, { headers: authHeader() });
      toast.success('Maintenance logged');
      setMaintOpen(false); setMaintNote(''); setMaintDate('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete asset ${asset.asset_tag}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/assets/${id}`, { headers: authHeader() });
      toast.success('Deleted');
      navigate('/assets/list');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const printLabel = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/assets/labels.pdf?ids=${id}`,
                                 { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${asset.asset_tag}-label.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error('Label generation failed'); }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!asset) return <div className="p-12 text-center text-[#64748B]">Asset not found</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="asset-detail">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-[#0E7490] hover:underline">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={printLabel} data-testid="print-label-btn"><Printer className="w-4 h-4 mr-2" /> Print label</Button>
          {asset.status === 'assigned' ? (
            <Button variant="outline" size="sm" onClick={checkin} disabled={busy} data-testid="checkin-btn">
              <LogIn className="w-4 h-4 mr-2" /> Check in
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setCheckoutOpen(true)} disabled={busy} data-testid="checkout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Check out
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setMaintOpen(true)} disabled={busy} data-testid="maint-btn">
            <Wrench className="w-4 h-4 mr-2" /> Log maintenance
          </Button>
          <Button variant="ghost" size="sm" onClick={remove} className="text-rose-600" data-testid="delete-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Main */}
        <div className="space-y-4">
          <Card className="p-5 border-[#E2E8F0]">
            <div className="flex items-center gap-2 text-xs text-[#64748B] mb-1">
              <Hash className="w-3 h-3" /> <span className="font-mono">{asset.asset_tag}</span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl font-bold text-[#0A2342]">{asset.name}</h1>
              <Badge className={`${STATUS_COLORS[asset.status]} border`}>{(asset.status || '').replace('_', ' ')}</Badge>
            </div>
            {asset.description && <p className="text-sm text-[#475569] mt-2">{asset.description}</p>}
            <div className="flex gap-3 mt-3 text-xs text-[#64748B]">
              {asset.category_name && <span>{asset.category_parent_name ? asset.category_parent_name + ' / ' : ''}{asset.category_name}</span>}
              {asset.manufacturer && <span>· {asset.manufacturer}{asset.model ? ` ${asset.model}` : ''}</span>}
              {asset.serial_number && <span>· S/N {asset.serial_number}</span>}
            </div>
          </Card>

          <Card className="border-[#E2E8F0]">
            <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="font-semibold text-[#0A2342]">Asset details</h3>
              {!editing ? <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="edit-btn"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                : (<div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="w-4 h-4" /></Button>
                    <Button size="sm" onClick={save} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-btn">
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                  </div>)}
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <DetailField label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} editing={editing} />
              <SelectField label="Category" value={form.category_id} onChange={v => setForm({ ...form, category_id: v })} editing={editing}
                           options={categories.filter(c => c.parent_id).map(c => ({ value: c.id, label: c.name }))} />
              <DetailField label="Manufacturer" value={form.manufacturer} onChange={v => setForm({ ...form, manufacturer: v })} editing={editing} />
              <DetailField label="Model" value={form.model} onChange={v => setForm({ ...form, model: v })} editing={editing} />
              <DetailField label="Serial Number" value={form.serial_number} onChange={v => setForm({ ...form, serial_number: v })} editing={editing} />
              <DetailField label="Vendor" value={form.vendor} onChange={v => setForm({ ...form, vendor: v })} editing={editing} />
              <DetailField label="Purchase Date" type="date" value={form.purchase_date} onChange={v => setForm({ ...form, purchase_date: v })} editing={editing} />
              <DetailField label="Purchase Cost ($)" type="number" value={form.purchase_cost} onChange={v => setForm({ ...form, purchase_cost: v })} editing={editing} />
              <DetailField label="Warranty Expires" type="date" value={form.warranty_expires_at} onChange={v => setForm({ ...form, warranty_expires_at: v })} editing={editing} />
              <DetailField label="PO #" value={form.purchase_order} onChange={v => setForm({ ...form, purchase_order: v })} editing={editing} />
            </div>
          </Card>

          {/* History */}
          {(asset.history || []).length > 0 && (
            <Card className="p-4 border-[#E2E8F0]">
              <h3 className="font-semibold text-[#0A2342] mb-3 text-sm flex items-center gap-2">
                <History className="w-4 h-4 text-[#0E7490]" /> History
              </h3>
              <ul className="space-y-2">
                {asset.history.map(h => (
                  <li key={h.id} className="text-xs text-[#64748B] flex items-start gap-2">
                    <span className="text-[#94A3B8] mt-0.5">•</span>
                    <span>
                      <strong className="text-[#0A2342]">{h.actor_name}</strong>{' '}
                      <HistoryLabel h={h} />{' '}
                      <span className="text-[#94A3B8]">— {new Date(h.created_at).toLocaleString()}</span>
                      {h.note && <div className="text-[#64748B] ml-3 mt-0.5 italic">"{h.note}"</div>}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Related Lists — owner's tickets, maintenance + assignment history */}
          <RelatedLists scope="asset" id={id} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {asset.assigned_to_user_name && (
            <Card className="p-4 border-[#E2E8F0]">
              <h3 className="text-xs uppercase text-[#64748B] mb-2 flex items-center gap-1"><User className="w-3 h-3" /> Assigned to</h3>
              <p className="text-sm font-medium text-[#0A2342]">{asset.assigned_to_user_name}</p>
              {asset.assigned_at && <p className="text-xs text-[#94A3B8] mt-1">Since {new Date(asset.assigned_at).toLocaleDateString()}</p>}
            </Card>
          )}

          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs uppercase text-[#64748B] mb-2 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Financials</h3>
            <Stat label="Cost" value={asset.purchase_cost ? `$${asset.purchase_cost.toLocaleString()}` : '—'} />
            <Stat label="Vendor" value={asset.vendor || '—'} />
            <Stat label="PO" value={asset.purchase_order || '—'} />
          </Card>

          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs uppercase text-[#64748B] mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> Warranty</h3>
            {asset.warranty_expires_at ? (
              <>
                <p className="text-sm text-[#0A2342]">{new Date(asset.warranty_expires_at).toLocaleDateString()}</p>
                {asset.warranty_expired
                  ? <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-xs mt-1">Expired</Badge>
                  : asset.warranty_expiring_soon
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs mt-1">Expires in {asset.warranty_days_remaining}d</Badge>
                    : <p className="text-xs text-[#94A3B8] mt-1">{asset.warranty_days_remaining}d remaining</p>}
              </>
            ) : <p className="text-xs text-[#94A3B8]">Not tracked</p>}
          </Card>
        </aside>
      </div>

      {/* Checkout dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle>Check out asset</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <Label>Assign to user</Label>
            <select value={checkoutUser} onChange={e => setCheckoutUser(e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white" data-testid="checkout-user">
              <option value="">Pick a user…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)}>Cancel</Button>
            <Button onClick={checkout} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="confirm-checkout">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Check out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance dialog */}
      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogContent data-testid="maint-dialog">
          <DialogHeader><DialogTitle>Log maintenance</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label>Maintenance note</Label>
              <textarea rows={3} value={maintNote} onChange={e => setMaintNote(e.target.value)}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="maint-note" /></div>
            <div><Label>Next service date (optional)</Label>
              <Input type="date" value={maintDate} onChange={e => setMaintDate(e.target.value)} data-testid="maint-date" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintOpen(false)}>Cancel</Button>
            <Button onClick={submitMaint} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="confirm-maint">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />} Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ label, value, onChange, editing, type = 'text' }) {
  return (
    <div>
      <div className="text-xs text-[#64748B] mb-1">{label}</div>
      {editing ? <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
        : <div className="text-sm text-[#0A2342]">{value || '—'}</div>}
    </div>
  );
}
function SelectField({ label, value, onChange, editing, options }) {
  return (
    <div>
      <div className="text-xs text-[#64748B] mb-1">{label}</div>
      {editing ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className="w-full h-8 text-sm rounded-md border border-[#E2E8F0] px-2 bg-white">
          <option value="">—</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : <div className="text-sm text-[#0A2342]">{options.find(o => o.value === value)?.label || '—'}</div>}
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs py-1">
      <span className="text-[#64748B]">{label}</span>
      <span className="text-[#0A2342] font-medium">{value}</span>
    </div>
  );
}
function HistoryLabel({ h }) {
  switch (h.action) {
    case 'created': return <>created asset</>;
    case 'status_changed': return <>changed status <strong>{h.from}</strong> → <strong>{h.to}</strong></>;
    case 'checked_out': return <>checked out</>;
    case 'checked_in': return <>checked in</>;
    case 'maintenance': return <>logged maintenance</>;
    default: return <>{h.action}</>;
  }
}
